/**
 * PropertyDetailPage Component
 * Shows property details with tabs: Overview, Documents, Expenses.
 * Mobile-first: prioritized information hierarchy.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Pencil, Trash2, Plus } from 'lucide-react';
import {
  getProperty,
  updateProperty,
  deleteProperty,
  listUnits,
  expensesApi,
  getPropertyBankAccount,
  setPropertyBankAccount,
  deletePropertyBankAccount,
  type PropertyWithStats,
  type UnitWithRelations,
  type PropertyBankAccount,
} from '../lib/api';
import { fetchLinkedBankAccounts, type LinkedBankAccount } from '../api/linked-bank-accounts.api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RulesSection } from '../components/rules/RulesSection';
import { DocumentsSection } from '../components/documents/DocumentsSection';
import { ExpensesSection } from '../components/expenses/ExpensesSection';
import { PageHeader, PageSection, DataGrid, DataRow, StatusBadge, EmptyState, ListItem } from '../components/layout/index';
import { EditableDataRow } from '../components/EditableDataRow';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { DetailTabs, TabsContent } from '../components/layout';
import { Badge } from '../components/ui/badge';
import { PropertyTypeLabel } from '../lib/constants';
import { formatUnitLabel } from '@resida/shared';

const EDITABLE_PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'multi_family', label: 'Multi Family' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'condo', label: 'Condominium' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'other', label: 'Other' },
] as const;

export function PropertyDetailPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();

  const [property, setProperty] = useState<PropertyWithStats | null>(null);
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingReviewCount, setPendingReviewCount] = useState<number | null>(null);
  const [propertyBankAccount, setPropertyBankAccountState] = useState<PropertyBankAccount | null>(null);
  const [linkedBankAccounts, setLinkedBankAccounts] = useState<LinkedBankAccount[]>([]);
  const [isBankAccountSaving, setIsBankAccountSaving] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!propertyId) return;

    try {
      setIsLoading(true);
      setError(null);

      const [propertyRes, unitsRes] = await Promise.all([
        getProperty(propertyId),
        listUnits({ propertyId }),
      ]);

      setProperty(propertyRes.property);
      setUnits(unitsRes.units);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load property');
    } finally {
      setIsLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch pending review count for this property
  useEffect(() => {
    if (!propertyId) return;
    expensesApi.getReviewQueue()
      .then((res) => {
        const count = res.expenses.filter(
          (e) => e.aiPropertyIdSuggested === propertyId
        ).length;
        setPendingReviewCount(count);
      })
      .catch(() => setPendingReviewCount(null));
  }, [propertyId]);

  // Fetch property bank account and linked bank accounts
  useEffect(() => {
    if (!propertyId) return;
    getPropertyBankAccount(propertyId)
      .then((account) => {
        setPropertyBankAccountState(account);
        setSelectedBankAccountId(account?.linkedBankAccountId ?? '');
      })
      .catch(() => setPropertyBankAccountState(null));
    fetchLinkedBankAccounts()
      .then((res) => setLinkedBankAccounts(res.accounts))
      .catch(() => setLinkedBankAccounts([]));
  }, [propertyId]);

  // Redirect single family properties directly to unit detail page
  useEffect(() => {
    if (property?.propertyType === 'single_family' && units.length === 1 && units[0]) {
      navigate(`/units/${units[0].id}`, { replace: true });
    }
  }, [property, units, navigate]);

  const handleDelete = async () => {
    if (!propertyId || !property) return;

    try {
      setIsDeleting(true);
      await deleteProperty(propertyId);
      navigate(`/portfolios/${property.portfolioId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete property');
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveBankAccount = async () => {
    if (!propertyId || !selectedBankAccountId) return;
    try {
      setIsBankAccountSaving(true);
      const account = await setPropertyBankAccount(propertyId, selectedBankAccountId);
      setPropertyBankAccountState(account);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save bank account');
    } finally {
      setIsBankAccountSaving(false);
    }
  };

  const handleRemoveBankAccount = async () => {
    if (!propertyId) return;
    try {
      setIsBankAccountSaving(true);
      await deletePropertyBankAccount(propertyId);
      setPropertyBankAccountState(null);
      setSelectedBankAccountId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove bank account');
    } finally {
      setIsBankAccountSaving(false);
    }
  };

  if (isLoading) {
    return <PropertyDetailSkeleton />;
  }

  if (error && !property) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <EmptyState
          title="Error Loading Property"
          description={error}
          action={
            <div className="flex gap-2">
              <Button onClick={fetchData}>Retry</Button>
              <Button variant="outline" onClick={() => navigate('/properties')}>Back to Properties</Button>
            </div>
          }
        />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <EmptyState
          title="Property Not Found"
          action={<Button variant="outline" onClick={() => navigate('/properties')}>Back to Properties</Button>}
        />
      </div>
    );
  }

  const propertyTypeLabel = PropertyTypeLabel[property.propertyType] || property.propertyType;
  const canAddUnit = !(property.propertyType === 'single_family' && units.length >= 1);

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="property-detail-page">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title={property.name}
          subtitle={propertyTypeLabel}
          backLink={{ to: '/properties', label: 'Properties' }}
          action={
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { window.location.hash = 'details'; }}>
                <Pencil className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          }
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <p className="text-destructive m-0">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <DetailTabs
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'documents', label: 'Documents', testId: 'documents-tab' },
          { key: 'expenses', label: 'Expenses', testId: 'expenses-tab', badge: pendingReviewCount ?? undefined },
          { key: 'details', label: 'Details', testId: 'details-tab' },
        ]}
        defaultTab="overview"
      >

        {/* ── Overview Tab ── */}
        <TabsContent value="overview" className="mt-0">
          {/* Property Info */}
          <PageSection title="Details">
            <DataGrid columns={2}>
              <DataRow label="Units" value={String(property.unitCount ?? units.length)} />
              <DataRow label="Type" value={propertyTypeLabel} data-testid="property-type-row" />
              {property.yearBuilt && <DataRow label="Year Built" value={String(property.yearBuilt)} />}
              {property.squareFeet && <DataRow label="Sq Ft" value={property.squareFeet.toLocaleString()} />}
            </DataGrid>
            {property.portfolio && (
              <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
                <Link
                  to={`/portfolios/${property.portfolioId}`}
                  className="text-sm text-primary hover:underline"
                >
                  {property.portfolio.name} portfolio
                </Link>
              </div>
            )}
          </PageSection>

          {/* Units */}
          <PageSection
            title="Units"
            badge={units.length}
            action={
              canAddUnit ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/units/new?propertyId=${propertyId}`)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Add Unit</span>
                </Button>
              ) : undefined
            }
          >
            {property.propertyType === 'single_family' && units.length >= 1 && (
              <p className="px-[var(--page-padding-x)] md:px-4 text-xs text-warning">
                Maximum units reached for single family property
              </p>
            )}
            {units.length === 0 ? (
              <div className="px-[var(--page-padding-x)] md:px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground mb-3">No units in this property yet.</p>
                <Button onClick={() => navigate(`/units/new?propertyId=${propertyId}`)}>
                  Add Your First Unit
                </Button>
              </div>
            ) : (
              units.map((unit) => {
                const specs = [
                  unit.bedrooms != null && `${unit.bedrooms} bed`,
                  unit.bathrooms != null && `${unit.bathrooms} bath`,
                  unit.sqft && `${unit.sqft.toLocaleString()} sq ft`,
                ].filter(Boolean).join(', ');

                return (
                  <ListItem
                    key={unit.id}
                    to={`/units/${unit.id}`}
                    title={formatUnitLabel(unit.unitNumber) ?? unit.address1}
                    subtitle={specs || undefined}
                    trailing={
                      <StatusBadge
                        status={unit.occupancy === 'available' ? 'vacant' : 'occupied'}
                        size="sm"
                      />
                    }
                  />
                );
              })
            )}
          </PageSection>

          {/* Bank Account */}
          {propertyId && (
            <PageSection
              title="Bank Account"
              data-testid="property-bank-account-section"
            >
              <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground m-0">
                    Link a bank account for external payment detection.
                  </p>
                  {propertyBankAccount?.isActive ? (
                    <Badge variant="secondary" className="bg-success/10 text-success">Linked</Badge>
                  ) : (
                    <Badge variant="secondary">Not linked</Badge>
                  )}
                </div>

                {linkedBankAccounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No linked bank accounts found.{' '}
                    <Link to="/external-payments/bank-accounts" className="text-primary hover:underline">
                      Link a bank account
                    </Link>{' '}
                    to get started.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="property-bank-account-select"
                        className="text-sm font-medium text-foreground"
                      >
                        Select Bank Account
                      </label>
                      <select
                        id="property-bank-account-select"
                        data-testid="property-bank-account-select"
                        value={selectedBankAccountId}
                        onChange={(e) => setSelectedBankAccountId(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
                      >
                        <option value="">None selected</option>
                        {linkedBankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.institutionName} — {account.accountName} ····{account.accountMask}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-2 flex-wrap mt-2">
                      <Button
                        size="sm"
                        data-testid="save-property-bank-account"
                        onClick={handleSaveBankAccount}
                        disabled={isBankAccountSaving || !selectedBankAccountId}
                      >
                        {isBankAccountSaving ? 'Saving...' : 'Save'}
                      </Button>
                      {propertyBankAccount && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="remove-property-bank-account"
                          onClick={handleRemoveBankAccount}
                          disabled={isBankAccountSaving}
                          className="text-destructive hover:text-destructive"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </>
                )}

                <div className="flex gap-2 flex-wrap mt-3">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/properties/${propertyId}/external-payments/onboarding`}>
                      Set Up Matching
                    </Link>
                  </Button>
                  {propertyBankAccount?.isActive && (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/properties/${propertyId}/external-payments/transactions`}>
                        View Transactions
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </PageSection>
          )}
        </TabsContent>

        {/* ── Documents Tab ── */}
        <TabsContent value="documents" className="mt-0">
          <RulesSection scope="PROPERTY" propertyId={propertyId} />
          <DocumentsSection scope="PROPERTY" propertyId={propertyId} />
        </TabsContent>

        {/* ── Expenses Tab ── */}
        <TabsContent value="expenses" className="mt-0">
          {propertyId && (
            <>
              {pendingReviewCount !== null && pendingReviewCount > 0 && (
                <div
                  className="mx-[var(--page-padding-x)] mb-3 flex items-center gap-3 px-3 py-2 bg-warning/5 border border-warning/30 rounded-lg"
                  data-testid="property-pending-review-badge"
                >
                  <span className="text-sm font-medium text-warning">
                    {pendingReviewCount} expense{pendingReviewCount !== 1 ? 's' : ''} pending review
                  </span>
                  <Link
                    to="/expenses/confirm"
                    className="text-sm font-semibold text-warning hover:underline"
                    aria-label="Go to review queue"
                  >
                    Review Queue &rarr;
                  </Link>
                </div>
              )}
              <ExpensesSection propertyId={propertyId} />
            </>
          )}
        </TabsContent>

        {/* ── Details Tab (AC-D1-AC-D6) ── */}
        <TabsContent value="details" className="mt-0">
          <PageSection title="Details">
            <DataGrid columns={3}>
              <EditableDataRow
                label="Property Type"
                value={property.propertyType}
                type="select"
                options={EDITABLE_PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                data-testid="details-property-type"
                onSave={async (v) => {
                  const response = await updateProperty(propertyId!, {
                    propertyType: v as PropertyWithStats['propertyType'],
                  });
                  setProperty((prev) => (prev ? { ...prev, ...response.property } : null));
                }}
              />
              <EditableDataRow
                label="Year Built"
                value={(() => {
                  const yearBuilt = (property.metadata?.constructionYear as number | undefined) ?? property.yearBuilt;
                  return yearBuilt != null ? String(yearBuilt) : '';
                })()}
                type="number"
                data-testid="details-year-built"
                onSave={async (v) => {
                  const metadata: Record<string, unknown> = { ...property.metadata };
                  if (v.trim()) {
                    metadata.constructionYear = parseInt(v, 10);
                  } else {
                    delete metadata.constructionYear;
                  }
                  const response = await updateProperty(propertyId!, { metadata });
                  setProperty((prev) => (prev ? { ...prev, ...response.property } : null));
                }}
              />
              <EditableDataRow
                label="Square Feet"
                value={(() => {
                  const squareFeet = (property.metadata?.squareFeet as number | undefined) ?? property.squareFeet;
                  return squareFeet != null ? String(squareFeet) : '';
                })()}
                type="number"
                data-testid="details-square-feet"
                onSave={async (v) => {
                  const metadata: Record<string, unknown> = { ...property.metadata };
                  if (v.trim()) {
                    metadata.squareFeet = parseInt(v, 10);
                  } else {
                    delete metadata.squareFeet;
                  }
                  const response = await updateProperty(propertyId!, { metadata });
                  setProperty((prev) => (prev ? { ...prev, ...response.property } : null));
                }}
              />
            </DataGrid>
          </PageSection>
        </TabsContent>
      </DetailTabs>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Property"
        message={`Are you sure you want to delete this property? This will also delete all units in this property. This action cannot be undone.`}
        confirmText="Delete Property"
        isDestructive
        isLoading={isDeleting}
      />
    </div>
  );
}

/* ── Loading Skeleton ── */

function PropertyDetailSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32 mt-1" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 rounded-md" />
        ))}
      </div>
      <div className="px-[var(--page-padding-x)] mt-6 space-y-3">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    </div>
  );
}

export default PropertyDetailPage;
